import React, { FC, useEffect, useState } from 'react'
import { Alert, Box, Button, CircularProgress, DialogActions, DialogContent, DialogTitle, Grid, Paper, TextField, Typography } from '@mui/material'
import { ContractorTransferPreview } from 'src/api'
import { CollapsibleSection, Dialog } from 'src/components/ui'
import { ContractorTransferConflictList } from 'src/components/sections/contractorTransfer/contractorTransferConflictList'
import { ContractorTransferCounts } from 'src/components/sections/contractorTransfer/contractorTransferCounts'
import { WizardFooter } from './wizardParts'
import {
  getReadingsTotals,
  getSummaryHint,
  hasTransferContent,
  hasTransferInProgress,
  isSourceNameConfirmed,
  TransferWizardAction,
  TransferWizardState,
  TransferWizardStep
} from './transferWizardHelpers'

const COUNT_LABELS: [keyof ContractorTransferPreview['counts'], string][] = [
  ['users', 'Users'],
  ['projects', 'Projects'],
  ['sensors', 'Sensors'],
  ['gateways', 'Gateways'],
  ['wagnerProbes', 'Wagner probes'],
  ['dataLoggers', 'Data loggers'],
  ['reports', 'Reports'],
  ['contractorTasks', 'Tasks without a project'],
  ['taskReassignments', 'Task assignees changed'],
  ['scheduleChanges', 'Scheduled reports changed'],
  ['projectRowsClosed', 'Project memberships closed'],
  ['projectRowsCreated', 'Project memberships created']
]

export const getPreviewTotals = (preview?: ContractorTransferPreview): { label: string; value: number }[] =>
  preview ? COUNT_LABELS.map(([key, label]) => ({ label, value: Number(preview.counts[key]) || 0 })) : []

interface SummaryStepProps {
  state: TransferWizardState
  dispatch: (action: TransferWizardAction) => void
  preview?: ContractorTransferPreview
  isPreviewLoading: boolean
  previewError?: string
  confirmOpen: boolean
  onOpenConfirm: () => void
  onCloseConfirm: () => void
  onRun: () => void
  isRunning: boolean
  onCheckAgain: () => void
}

/** Step 5: the counts, warnings and blocking errors of the preview; a blocking error disables Run. */
export const SummaryStep: FC<SummaryStepProps> = ({
  state,
  dispatch,
  preview,
  isPreviewLoading,
  previewError,
  confirmOpen,
  onOpenConfirm,
  onCloseConfirm,
  onRun,
  isRunning,
  onCheckAgain
}) => {
  const errors = preview?.errors || []
  const warnings = preview?.warnings || []
  const totals = getPreviewTotals(preview)
  const readingsTotals = getReadingsTotals(preview)
  const notEstimated = preview?.readings?.notEstimated ?? []
  const [typedName, setTypedName] = useState('')
  const nameConfirmed = isSourceNameConfirmed(typedName, preview?.source.name)

  useEffect(() => {
    // every opening of the dialog asks for the name again
    if (confirmOpen) setTypedName('')
  }, [confirmOpen])
  const nothingToMove = !hasTransferContent(state)
  const runDisabled = nothingToMove || isPreviewLoading || Boolean(previewError) || !preview || errors.length > 0

  const hint = getSummaryHint(errors, nothingToMove)
  const waitingForOtherTransfer = hasTransferInProgress(errors)

  return (
    <>
      {previewError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {previewError}
        </Alert>
      )}
      {isPreviewLoading && (
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <CircularProgress size={20} />
          <Typography>Building the impact report…</Typography>
        </Box>
      )}
      {preview && (
        <>
          <Typography mb={2}>
            Move from <b>{preview.source.name}</b> to <b>{preview.target.name}</b>:
          </Typography>
          <Grid container spacing={2} mb={3}>
            {totals.map((total) => (
              <Grid item xs={6} sm={3} key={total.label}>
                <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                  <Typography variant="h6">{total.value.toLocaleString()}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {total.label}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
          {readingsTotals.length > 0 && (
            <>
              <Typography variant="subtitle1" fontWeight={500} mb={1}>
                After the move
              </Typography>
              <Grid container spacing={2} mb={3}>
                {readingsTotals.map((total) => (
                  <Grid item xs={6} sm={3} key={total.label}>
                    <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                      <Typography variant="h6">{total.value.toLocaleString()}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {total.label}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              {notEstimated.length > 0 && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  {`Not estimated in time: ${notEstimated.join(', ')}. The estimate leaves these tables out; the transfer rewrites their readings all the same.`}
                </Alert>
              )}
            </>
          )}
          {errors.length > 0 && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                waitingForOtherTransfer ? (
                  <Button color="inherit" size="small" disabled={isPreviewLoading} onClick={onCheckAgain}>
                    Check again
                  </Button>
                ) : undefined
              }
            >
              {`${errors.length} blocking ${errors.length === 1 ? 'error stops' : 'errors stop'} this transfer.`}
            </Alert>
          )}
          <Box mb={2}>
            <CollapsibleSection title={`Blocking errors and warnings (${errors.length + warnings.length})`} defaultExpanded={errors.length + warnings.length > 0}>
              <ContractorTransferConflictList conflicts={[...errors, ...warnings]} exportedFileName={`${document.title}_conflicts`} />
            </CollapsibleSection>
            <CollapsibleSection title="Rows per table">
              <ContractorTransferCounts counts={preview.counts.tables || {}} exportedFileName={`${document.title}_rows`} />
            </CollapsibleSection>
            {preview.readings && (
              <CollapsibleSection title="Readings per table (estimate)">
                <ContractorTransferCounts counts={preview.readings.estimatedRows || {}} exportedFileName={`${document.title}_readings`} />
              </CollapsibleSection>
            )}
          </Box>
        </>
      )}
      <WizardFooter
        hint={hint}
        onBack={() => dispatch({ type: 'goTo', step: TransferWizardStep.Devices })}
        nextLabel="Run transfer"
        nextColor="error"
        nextDisabled={runDisabled}
        onNext={onOpenConfirm}
      />
      <Dialog
        open={confirmOpen}
        onClose={onCloseConfirm}
        wrapContent={false}
        DialogProps={{ maxWidth: 'sm', fullWidth: true }}
        title={<DialogTitle>Run the transfer</DialogTitle>}
        actions={
          <DialogActions>
            <Button variant="outlined" onClick={onCloseConfirm}>
              Cancel
            </Button>
            <Button variant="contained" color="error" disabled={isRunning || !nameConfirmed} onClick={onRun}>
              {isRunning ? <CircularProgress color="info" size={26} /> : 'Transfer'}
            </Button>
          </DialogActions>
        }
      >
        <DialogContent>
          {preview && (
            <>
              <Typography mb={1}>
                Move from <b>{preview.source.name}</b> to <b>{preview.target.name}</b>:
              </Typography>
              <Typography component="ul" sx={{ pl: 3, my: 1 }}>
                {totals
                  .filter((total) => total.value > 0)
                  .map((total) => (
                    <li key={total.label}>{`${total.label}: ${total.value.toLocaleString()}`}</li>
                  ))}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                The moved projects are frozen and the moved users are signed out for about a minute. The transfer cannot be undone from here.
              </Typography>
              <TextField
                fullWidth
                size="small"
                autoFocus
                autoComplete="off"
                label="Source contractor name"
                value={typedName}
                onChange={(event) => setTypedName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && nameConfirmed && !isRunning) onRun()
                }}
                helperText={
                  <>
                    Type <b>{preview.source.name}</b> to confirm.
                  </>
                }
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
